import { useCallback } from 'react'

import { useQuery, useSuspenseQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import {
  PRESIGNED_URL_REFETCH_INTERVAL,
  PRESIGNED_URL_STALE_TIME,
} from '@/hooks/opportunity/presignedFileUrls'
import {
  buildOpportunityDetailQueryString,
  type OpportunityDetailFetchOptions,
} from '@/hooks/opportunity/queryParams'

import type {
  FederalContract,
  FederalNotice,
  LinkedAwardsResponse,
} from '@/types/federal-contract/FederalContract'
import type { UUID } from '@/types/Id'
import type { SearchOut } from '@/types/Search'

export const fetchFederalContract = async (
  federalContractId: string,
  options?: OpportunityDetailFetchOptions,
): Promise<FederalContract | null> => {
  if (federalContractId === '') {
    return null
  }
  const params = buildOpportunityDetailQueryString(options)
  const url = `${API_URL}/api/v1/federal-contracts/${federalContractId}${params}`
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok) {
    throw res
  }
  return await res.json()
}

export const federalContractWithoutPresignedUrlsQueryKey = (
  federalContractId: string,
) => [federalContractId, 'federalContract', 'withoutPresignedUrls'] as const

export const fetchFederalContractWithoutPresignedUrls = (
  federalContractId: string,
) => fetchFederalContract(federalContractId, { includePresignedUrls: false })

export const useSuspenseFederalContractWithoutPresignedUrls = (
  federalContractId: string,
) => {
  return useSuspenseQuery({
    queryKey: federalContractWithoutPresignedUrlsQueryKey(federalContractId),
    queryFn: async () => {
      const data =
        await fetchFederalContractWithoutPresignedUrls(federalContractId)
      if (!data) throw new Response(null, { status: 404 })
      return data
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}

export const federalContractPresignedUrlsQueryKey = (
  federalContractId: string,
) => [federalContractId, 'federalContract', 'presignedUrls'] as const

// Live client-side fetch with presigned file URLs; the page shows it in place of the ISR copy.
export const useFederalContractPresignedUrls = (federalContractId: string) => {
  return useQuery({
    queryKey: federalContractPresignedUrlsQueryKey(federalContractId),
    queryFn: () => fetchFederalContract(federalContractId),
    staleTime: PRESIGNED_URL_STALE_TIME,
    refetchInterval: PRESIGNED_URL_REFETCH_INTERVAL,
    enabled: federalContractId !== '',
  })
}

export const useFederalContract = (
  federalContractId: string,
  initialData?: FederalContract,
) => {
  return useQuery({
    queryKey: [federalContractId, 'federalContract'],
    queryFn: () => fetchFederalContract(federalContractId),
    initialData: initialData,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: federalContractId !== '',
  })
}

/**
 * Fetch only linked USASpending awards for a federal contract.
 * This is designed for progressive loading - use alongside useFederalContract
 * to show the main contract data immediately while awards load separately.
 */
export const fetchFederalContractLinkedAwards = async (
  federalContractId: string,
): Promise<LinkedAwardsResponse | null> => {
  if (federalContractId === '') {
    return null
  }
  const url = `${API_URL}/api/v1/federal-contracts/${federalContractId}/linked-awards`
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok) {
    throw res
  }
  return await res.json()
}

export const useFederalContractLinkedAwards = (
  federalContractId: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: [federalContractId, 'federalContractLinkedAwards'],
    queryFn: () => fetchFederalContractLinkedAwards(federalContractId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: federalContractId !== '' && (options?.enabled ?? true),
  })
}

export const useFederalContractLatestNotice = (
  federalContractId: UUID | '',
) => {
  const selectLatestNotice = useCallback(
    (data: FederalContract | null): FederalNotice | null => {
      if (!data || !data.notices || data.notices.length === 0) {
        return null
      }

      // Sort notices by posted_date in descending order and get the first one
      const latestNotice = [...data.notices].sort(
        (a, b) =>
          new Date(b.posted_date).getTime() - new Date(a.posted_date).getTime(),
      )[0]

      return latestNotice
    },
    [],
  )

  return useQuery({
    queryKey: [federalContractId, 'federalContract'],
    queryFn: () => fetchFederalContract(federalContractId),
    select: selectLatestNotice,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: federalContractId !== '',
  })
}

export const fetchSimilarFederalContracts = async (
  federalContractId: string,
): Promise<Array<SearchOut>> => {
  if (federalContractId === '') {
    return []
  }
  const url = `${API_URL}/api/v1/federal-contracts/${federalContractId}/similar`
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok) {
    throw res
  }
  return await res.json()
}

export const useSimilarFederalContracts = (federalContractId: string) => {
  return useQuery({
    queryKey: [federalContractId, 'similarFederalContracts'],
    queryFn: () => fetchSimilarFederalContracts(federalContractId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: federalContractId !== '',
  })
}
